import { useState, useMemo, useEffect } from "react";
import { X, Check, AlertTriangle, Layers } from "lucide-react";
import MediaFrame from "@/components/common/MediaFrame";
import { t } from "@/i18n";

/**
 * ReferenceLimitModal
 * Modal de resolución cuando el total de referencias supera el límite del modelo (p. ej. máx. 2).
 * Permite al usuario elegir conscientemente cuáles referencias conservar, sin descartes silenciosos.
 */
export const ReferenceLimitModal = ({
  open,
  onClose,
  maxAllowed = 2,
  currentReferences = [],
  candidateReferences = [],
  assetById = {},
  onConfirm,
}) => {
  // Combinar y deduplicar por assetId
  const allItems = useMemo(() => {
    const list = [];
    const seenAssetIds = new Set();

    for (const ref of [...currentReferences, ...candidateReferences]) {
      if (ref && ref.assetId && !seenAssetIds.has(ref.assetId)) {
        seenAssetIds.add(ref.assetId);
        list.push(ref);
      }
    }
    return list;
  }, [currentReferences, candidateReferences]);

  // Estado local de seleccionadas (por defecto, las primeras maxAllowed)
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);

  useEffect(() => {
    if (open) {
      const initial = [];
      for (const item of [...candidateReferences, ...currentReferences]) {
        if (item?.assetId && !initial.includes(item.assetId) && initial.length < maxAllowed) {
          initial.push(item.assetId);
        }
      }
      setSelectedAssetIds(initial);
    }
  }, [open, candidateReferences, currentReferences, maxAllowed]);

  if (!open) return null;

  const toggleSelect = (assetId) => {
    setSelectedAssetIds((prev) => {
      if (prev.includes(assetId)) {
        return prev.filter((id) => id !== assetId);
      }
      if (prev.length >= maxAllowed) {
        // Si ya está en maxAllowed y selecciona otra, reemplazar la más antigua
        // o requerir desmarcar primero
        return [...prev.slice(1), assetId];
      }
      return [...prev, assetId];
    });
  };

  const handleConfirm = () => {
    onConfirm?.(selectedAssetIds);
    onClose?.();
  };

  const isOverLimit = selectedAssetIds.length > maxAllowed;
  const isNoneSelected = selectedAssetIds.length === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="reference-limit-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm animate-fade-in"
    >
      <div className="relative flex flex-col w-full max-w-lg rounded-xl border border-[#3a3a3a] bg-[#141414] shadow-2xl overflow-hidden max-h-[85vh]">
        {/* Cabecera con tono de advertencia */}
        <div className="flex items-center justify-between border-b border-[#242424] px-5 py-4 bg-[#1a1714]">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
              <AlertTriangle size={18} />
            </div>
            <div className="flex flex-col">
              <h2 className="text-sm font-semibold text-[#f4f1ec]">
                {t.tools?.refsLimitModalTitle || `Límite de referencias alcanzado (máx. ${maxAllowed})`}
              </h2>
              <p className="text-[11px] text-[#999]">
                {t.tools?.refsLimitModalSubtitle ||
                  `El modelo actual permite un máximo de ${maxAllowed} referencias. Selecciona cuáles deseas conservar:`}
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="reference-limit-modal-close"
            onClick={onClose}
            aria-label={t.common?.close || "Cerrar"}
            className="rounded-lg p-1.5 text-[#888] hover:bg-[#252525] hover:text-[#f4f1ec] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="flex items-center justify-between text-xs text-[#a0a0a0] mb-3">
            <span>{t.tools?.selectRefsToKeep || "Elige las referencias a conservar:"}</span>
            <span
              className={`font-mono font-medium ${
                selectedAssetIds.length === maxAllowed
                  ? "text-[#f5b544]"
                  : selectedAssetIds.length > maxAllowed
                  ? "text-red-400"
                  : "text-[#888]"
              }`}
            >
              {selectedAssetIds.length} / {maxAllowed}
            </span>
          </div>

          <div
            data-testid="reference-limit-items-list"
            className="flex flex-col gap-2.5"
          >
            {allItems.map((item) => {
              const isSelected = selectedAssetIds.includes(item.assetId);
              const asset = assetById[item.assetId];
              const isCandidate = candidateReferences.some((c) => c.assetId === item.assetId);

              return (
                <div
                  key={item.assetId}
                  data-testid={`reference-limit-item-${item.assetId}`}
                  onClick={() => toggleSelect(item.assetId)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && toggleSelect(item.assetId)}
                  className={`flex items-center justify-between p-2.5 rounded-lg border transition-all cursor-pointer ${
                    isSelected
                      ? "border-[#f5b544] bg-[#f5b544]/10 shadow-sm"
                      : "border-[#262626] bg-[#171717] hover:border-[#383838] hover:bg-[#1c1c1c]"
                  }`}
                >
                  <div className="flex items-center gap-3 overflow-hidden">
                    <MediaFrame
                      asset={asset}
                      fit="contain"
                      className="h-12 w-12 rounded border border-white/10 shrink-0 bg-[#0d0d0d]"
                      emptyMessage="—"
                    />
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {item.isCharacter ? (
                          <span className="font-mono text-[10px] px-1.5 py-0.2 rounded bg-purple-500/15 border border-purple-500/30 text-purple-300 font-medium">
                            @{item.characterHandle}
                          </span>
                        ) : (
                          <span className="font-mono text-[10px] px-1.5 py-0.2 rounded bg-[#333] text-[#aaa]">
                            {t.tools?.manualRef || "Manual"}
                          </span>
                        )}
                        {isCandidate && (
                          <span className="text-[9px] px-1 rounded bg-[#f5b544]/20 text-[#f5b544] font-medium">
                            {t.tools?.candidateRef || "Nueva"}
                          </span>
                        )}
                      </div>
                      <span className="text-xs font-medium text-[#ededed] truncate mt-0.5">
                        {item.viewLabel || item.label || asset?.label || asset?.filename || t.common?.untitled}
                      </span>
                    </div>
                  </div>

                  {/* Checkbox circular */}
                  <div
                    className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 ml-3 transition-all ${
                      isSelected
                        ? "bg-[#f5b544] text-black shadow"
                        : "border border-white/30 bg-black/30 text-transparent"
                    }`}
                  >
                    <Check size={12} strokeWidth={3} />
                  </div>
                </div>
              );
            })}
          </div>

          {isOverLimit && (
            <p className="mt-3 text-xs text-red-400 font-medium">
              {t.tools?.overLimitWarning?.(maxAllowed) ||
                `Has seleccionado más de ${maxAllowed} referencias. Desmarca alguna para continuar.`}
            </p>
          )}
        </div>

        {/* Pie fijo */}
        <div className="flex items-center justify-between border-t border-[#242424] px-5 py-3.5 bg-[#181818]">
          <button
            type="button"
            data-testid="reference-limit-modal-cancel"
            onClick={onClose}
            className="v-btn-ghost v-btn-sm text-xs text-[#a0a0a0] hover:text-[#f4f1ec]"
          >
            {t.common?.cancel || "Cancelar"}
          </button>

          <button
            type="button"
            data-testid="reference-limit-modal-confirm"
            onClick={handleConfirm}
            disabled={isOverLimit || isNoneSelected}
            className="v-btn-primary v-btn-sm text-xs flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Check size={13} />
            <span>
              {t.tools?.confirmKeepRefs?.(selectedAssetIds.length) ||
                `Conservar ${selectedAssetIds.length} referencia${selectedAssetIds.length > 1 ? "s" : ""}`}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferenceLimitModal;
